import { formatResetDay } from '@/libs/utils/format-reset-day';
import { Stack, Text } from '@chakra-ui/react';
import { PlanLimit, type PlanQuotaMeta } from '@fluenceur/types';
import { Gift } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { PlanUsageCounter } from './PlanUsageCounter';
import { useQuotaUpgrade } from './use-quota-upgrade';

/**
 * "1 / 3 gifting invitations this month" for a plan that counts gifting
 * invitations. Renders nothing on a plan that counts none.
 */
export const GiftingQuotaCounter = ({ quota }: { quota: PlanQuotaMeta }) => {
  const { t } = useTranslation();
  const upgrade = useQuotaUpgrade(PlanLimit.GIFTING_INVITATIONS, quota);

  if (quota.limit === null) return null;
  const { used, limit } = quota;
  const resetDay = formatResetDay(quota.resetsAt, t('common.locale'));
  const isExhausted = used >= limit;

  return (
    <PlanUsageCounter
      used={used}
      limit={limit}
      unit={t('planUsage.giftingInvitations.unit')}
      icon={<Gift width={13} height={13} />}
      accent="green.100"
      hint={
        <Stack gap="4px" maxW="260px">
          {isExhausted && (
            <Text>
              {t('planUsage.giftingInvitations.exhausted', { limit, date: resetDay ?? '' })}
            </Text>
          )}
          {!isExhausted && resetDay && (
            <Text>{t('planUsage.giftingInvitations.resetsOn', { date: resetDay })}</Text>
          )}
          <Text>{t('planUsage.giftingInvitations.howItCounts')}</Text>
          {isExhausted && (
            <Text fontWeight="semibold">{t('planUsage.giftingInvitations.upgradeLink')}</Text>
          )}
        </Stack>
      }
      onUpgrade={upgrade}
    />
  );
};
